import {
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button as ButtonPrimitive, ButtonProps } from '@givebutter/primitives/button';
import { Checkbox } from '@givebutter/primitives/checkbox';
import { Label } from '@givebutter/primitives/label';
import { useLocalStorage } from 'contexts/useLocalStorage';

interface IgnoreAlertsContextValue {
    isChecked: boolean;
    setIsChecked: Dispatch<SetStateAction<boolean>>;
}
const IgnoreAlertsContext = createContext<IgnoreAlertsContextValue>({
    isChecked: false,
    setIsChecked: () => {},
});

interface IgnoreAlertsRootProps extends PropsWithChildren {}
const IgnoreAlertsRoot = ({ children }: IgnoreAlertsRootProps) => {
    const { hasIgnoredDataHygieneIgnoreAlerts } = useLocalStorage(
        ({ hasIgnoredDataHygieneIgnoreAlerts }) => ({
            hasIgnoredDataHygieneIgnoreAlerts,
        }),
    );
    // NOTE: if alert is suppressed, forcing AlertDialog component to be controlled (false) so the button does not act like a dialog trigger
    const isOpen = hasIgnoredDataHygieneIgnoreAlerts ? false : undefined;

    const [isChecked, setIsChecked] = useState(hasIgnoredDataHygieneIgnoreAlerts);
    const ctx = useMemo(() => ({ isChecked, setIsChecked }), [isChecked]);

    return (
        <IgnoreAlertsContext.Provider value={ctx}>
            <AlertDialog.Root open={isOpen} type="warning">
                {children}
            </AlertDialog.Root>
        </IgnoreAlertsContext.Provider>
    );
};

interface IgnoreAlertsTitleProps extends PropsWithChildren {}
const IgnoreAlertsTitle = ({ children }: IgnoreAlertsTitleProps) => {
    return <AlertDialog.Title>{children || 'Ignore issues?'}</AlertDialog.Title>;
};

interface IgnoreAlertsDescriptionProps extends PropsWithChildren {}
const IgnoreAlertsDescription = ({ children }: IgnoreAlertsDescriptionProps) => {
    return (
        <AlertDialog.Description>
            {children ||
                'Ignoring these issues will remove them for 90 days before they reappear. Alternatively, you can save an issue for later and view them separately.'}
        </AlertDialog.Description>
    );
};

interface IgnoreAlertsButtonProps extends Omit<ButtonProps, 'variant'> {}
const IgnoreAlertsButton = ({ size, disabled, onClick, ...rest }: IgnoreAlertsButtonProps) => {
    const { hasIgnoredDataHygieneIgnoreAlerts } = useLocalStorage(
        ({ hasIgnoredDataHygieneIgnoreAlerts }) => ({
            hasIgnoredDataHygieneIgnoreAlerts,
        }),
    );

    return (
        <AlertDialog.Trigger asChild={true}>
            <ButtonPrimitive
                size={size}
                variant={'outline'}
                color={'neutral'}
                disabled={disabled}
                className="!h-9 !px-3 !py-2 !text-sm"
                onClick={e => {
                    if (hasIgnoredDataHygieneIgnoreAlerts) {
                        e.preventDefault();
                        onClick?.(e);
                    }
                }}
                {...rest}
            />
        </AlertDialog.Trigger>
    );
};

interface IgnoreAlertsDialogContentProps extends PropsWithChildren {}
const IgnoreAlertsDialogContent = ({ children }: IgnoreAlertsDialogContentProps) => {
    return (
        <AlertDialog.Portal>
            <AlertDialog.Overlay />
            <AlertDialog.Content>
                <AlertDialog.Header>
                    <AlertDialog.Icon />
                    <AlertDialog.Close />
                </AlertDialog.Header>
                {children}
            </AlertDialog.Content>
        </AlertDialog.Portal>
    );
};

interface IgnoreAlertsAlertBodyProps extends PropsWithChildren {}
const IgnoreAlertsAlertBody = ({ children }: IgnoreAlertsAlertBodyProps) => {
    const { isChecked, setIsChecked } = useContext(IgnoreAlertsContext);
    return (
        <AlertDialog.Body className="gap-y-4">
            {children}
            <div>
                <Label className="flex items-center gap-3">
                    <Checkbox
                        checked={isChecked}
                        onCheckedChange={value => {
                            setIsChecked(value as boolean);
                        }}
                    />
                    <span>Don't show me this again</span>
                </Label>
            </div>
        </AlertDialog.Body>
    );
};

interface IgnoreAlertsAlertActionProps extends Omit<ButtonProps, 'children' | 'variant'> {}
const IgnoreAlertsAlertAction = ({ onClick, disabled, ...rest }: IgnoreAlertsAlertActionProps) => {
    const { isChecked } = useContext(IgnoreAlertsContext);
    const { setHasIgnoredDataHygieneIgnoreAlerts } = useLocalStorage(
        ({ setHasIgnoredDataHygieneIgnoreAlerts }) => ({
            setHasIgnoredDataHygieneIgnoreAlerts,
        }),
    );

    return (
        <AlertDialog.Footer>
            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action
                onClick={e => {
                    setHasIgnoredDataHygieneIgnoreAlerts(isChecked);
                    onClick?.(e);
                }}
                disabled={disabled}
                {...rest}
            >
                Ignore
            </AlertDialog.Action>
        </AlertDialog.Footer>
    );
};

const Root = IgnoreAlertsRoot;
const Button = IgnoreAlertsButton;
const AlertContent = IgnoreAlertsDialogContent;
const AlertBody = IgnoreAlertsAlertBody;
const AlertAction = IgnoreAlertsAlertAction;
const Title = IgnoreAlertsTitle;
const Description = IgnoreAlertsDescription;

export {
    Root,
    Button,
    AlertContent,
    AlertBody,
    AlertAction,
    Title,
    Description,
    //
    IgnoreAlertsRoot,
    IgnoreAlertsButton,
    IgnoreAlertsDialogContent,
    IgnoreAlertsAlertBody,
    IgnoreAlertsAlertAction,
    IgnoreAlertsTitle,
    IgnoreAlertsDescription,
};
